'use client'

import { useMutation, useQuery } from '@tanstack/react-query'

import type { ButtonProps } from '@ui/web/components'
import { Button, toast } from '@ui/web/components'

import { useAuthClient } from '../context'
import { useSharedTRPC } from '../context/trpc'

const LABEL = 'Hubungi admin'
const SENT_LABEL = 'Permintaan terkirim'

interface ContactAdminButtonBaseProps {
  onRequest: () => void
  loading?: boolean
  /** Post-send state. The server only counts one request per teacher per day anyway. */
  sent?: boolean
  variant?: ButtonProps['variant']
  className?: string
}

/**
 * Presentational half, so the button can be rendered without a tRPC provider (Storybook mounts
 * none, and `useSharedTRPC` throws unwrapped). Same split as `ShareContentDialogBase`.
 */
export function ContactAdminButtonBase({ onRequest, loading, sent, variant, className }: ContactAdminButtonBaseProps) {
  return (
    /*
      An action, not a link, and therefore without `asChild`: that branch renders a bare slot which
      forwards neither the spinner nor `disabled`, so a pending state is impossible there.
    */
    <Button
      type='button'
      variant={variant}
      className={className}
      loading={loading}
      disabled={loading || sent}
      onClick={onRequest}
    >
      {sent ? SENT_LABEL : LABEL}
    </Button>
  )
}

interface ContactAdminButtonProps {
  variant?: ButtonProps['variant']
  className?: string
}

/**
 * "Hubungi admin" — a teacher with no rombel asking the school's owners and admins to give them a
 * class. Requests roll up per school per day, so a second press folds into the card already sent
 * instead of writing another one; `alreadyCounted` is how the server says so, and it is a success.
 *
 * Renders `null` unless the caller really is a member of this school with no class. That gate lives
 * here rather than at each call site because the blocker copy is repeated across the four dashboard
 * tabs AND `NotAllowedOverlay`, and the overlay is also shown to a guru who teaches three classes —
 * for them there is nothing to ask for, and the mutation would reject it. Self-gating means the
 * button can be dropped into any of those surfaces without each one re-deriving who is blocked.
 *
 * Both gating queries are the ones the surrounding pages already run (`useActiveMemberRole` and
 * `myOrganization.getMyTeams`), so mounting this costs no extra request in practice.
 */
export function ContactAdminButton({ variant, className }: ContactAdminButtonProps) {
  const authClient = useAuthClient()
  const trpc = useSharedTRPC()

  const { data: memberRole, isPending: isRolePending } = authClient.useActiveMemberRole()
  const isAdminOrOwner = memberRole?.role === 'owner' || memberRole?.role === 'admin'

  const myTeamsQuery = useQuery({
    ...trpc.myOrganization.getMyTeams.queryOptions(),
    enabled: !isRolePending && !isAdminOrOwner,
  })

  const requestClass = useMutation(
    trpc.teacherAssignment.requestClass.mutationOptions({
      onSuccess: ({ alreadyCounted }) => {
        toast.success(
          alreadyCounted
            ? 'Permintaanmu sudah terkirim sebelumnya. Tunggu kabar dari admin ya!'
            : 'Permintaan terkirim ke admin sekolah.',
        )
      },
      onError: (error) => {
        toast.error(error.message || 'Gagal mengirim permintaan ke admin sekolah.')
      },
    }),
  )

  if (isRolePending || isAdminOrOwner) return null

  /**
   * Requires the classes to have actually loaded and to be empty. `isLoading` is false for a
   * disabled query, so a truthiness check on the data is what separates "no classes" from "not
   * asked yet" — showing the button on the latter would offer it to a guru who does teach.
   */
  const myTeams = myTeamsQuery.data
  if (!myTeams || myTeams.length > 0) return null

  return (
    <ContactAdminButtonBase
      onRequest={() => requestClass.mutate()}
      loading={requestClass.isPending}
      sent={requestClass.isSuccess}
      variant={variant}
      className={className}
    />
  )
}
